import React, { type ComponentPropsWithRef, type ComponentType } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type RouteObject, useNavigate } from "react-router-dom-v5-compat";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import {
  type AppDispatch,
  mapExternalResource,
  type TripProduct,
} from "@b2bportal/core-types";
import { Product } from "@b2bportal/purchase-api";
import {
  FlightItineraryCard,
  FlightItineraryDetailPage,
} from "@tenant/flights";
import dayjs from "dayjs";
import {
  ALL_FLIGHT_STATES,
  flightsPostBookingApi,
  FlightPostBookingSelectors,
} from "../store";

type FlightTripProduct = TripProduct<BookedFlightItineraryWithDepartureTime>;

export const useFlightTripProduct = ({
  tripsPath,
  reservationPath,
}: {
  tripsPath: string;
  reservationPath: (reservationId: string) => string;
}): {
  flights: { [Product.Flight]: FlightTripProduct };
  pastFlights: { [Product.Flight]: FlightTripProduct };
  flightDetailRouteProps: RouteObject;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const flightProduct: Omit<FlightTripProduct, "reservations"> = React.useMemo(
    () => ({
      fetch: () => {
        dispatch(
          flightsPostBookingApi.endpoints.fetchAllFlightItineraries.initiate({
            states: ALL_FLIGHT_STATES,
            referenceDateTime: dayjs().format(),
          })
        );
      },
      component: ({
        reservation,
        ...props
      }: ComponentPropsWithRef<
        ComponentType<{ reservation: BookedFlightItineraryWithDepartureTime }>
      >) => (
        <FlightItineraryCard
          {...props}
          selectItinerary={() =>
            navigate(reservationPath(reservation.bookedItinerary.id))
          }
          itinerary={reservation}
        />
      ),
    }),
    [dispatch, navigate, reservationPath]
  );

  const reservations = useSelector(
    FlightPostBookingSelectors.getFlightItineraries
  );

  return React.useMemo(
    () => ({
      flights: {
        [Product.Flight]: {
          ...flightProduct,
          reservations: mapExternalResource(
            reservations,
            (itineraries) =>
              [
                ...(itineraries.future ?? []),
                ...(itineraries.present ?? []),
              ].map((itinerary) => ({
                reservationId: itinerary.bookedItinerary.id,
                sortDate:
                  itinerary.bookedItinerary.travelItinerary.slices[0]
                    .segments[0].scheduledDeparture,
                recentFirst: false,
                reservation: itinerary,
                productType: Product.Flight,
              })),
            String
          ),
        },
      },
      pastFlights: {
        [Product.Flight]: {
          ...flightProduct,
          reservations: mapExternalResource(
            reservations,
            (itineraries) => [
              ...(itineraries.past ?? []).map((itinerary) => ({
                reservationId: itinerary.bookedItinerary.id,
                sortDate:
                  itinerary.bookedItinerary.travelItinerary.slices[0]
                    .segments[0].scheduledDeparture,
                recentFirst: true,
                reservation: itinerary,
                productType: Product.Flight,
              })),
              ...(itineraries.canceled ?? []).map((itinerary) => ({
                reservationId: itinerary.bookedItinerary.id,
                sortDate:
                  itinerary.bookedItinerary.travelItinerary.slices[0]
                    .segments[0].scheduledDeparture,
                recentFirst: true,
                isCancelled: true,
                reservation: itinerary,
                productType: Product.Flight,
              })),
            ],
            String
          ),
        },
      },
      flightDetailRouteProps: {
        path: reservationPath(":reservationId"),
        element: <FlightItineraryDetailPage tripsRoute={tripsPath} />,
      },
    }),
    [flightProduct, reservations, reservationPath, tripsPath]
  );
};
